import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, {
  css,
  type DefaultTheme,
  type FlattenInterpolation,
  type ThemeProps,
} from 'styled-components';
import { Flex, SideBar, ViewerContext } from 'dash-ui';

import renderBranch from 'common/helpers/renderBranch';
import BreadcrumbNavigation from 'flow-components/BreadcrumbNavigation';

import type { IContainerProps } from './Container.interfaces';
import { getSidebarItemsList } from './Container.helpers';
import { ContainerFlex } from 'flow-components/Container/ContainerFlex.style';

const ContainerWrapper = styled(Flex)`
  align-self: center;
  justify-content: center;
  width: 100%;
  flex-grow: 0;
`;

const ContainerStyle = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.n20};
  min-height: calc(100vh - 50px);
  box-sizing: border-box;
  max-width: calc(100% - 248px);
  @media ${({ theme }) => `${theme.devices.tablet}, ${theme.devices.mobile}`} {
    max-width: unset;
  }
`;
const BreadcrumbWrapper = styled<{ stickyBreadcrumbs: boolean } & any>(Flex)`
  background-color: inherit;

  ${({ stickyBreadcrumbs }) =>
    stickyBreadcrumbs
      ? css`
          top: 50px;
          position: sticky;
          z-index: 1;
        `
      : ''}
`;
const Content = styled(Flex)<{
  css?: FlattenInterpolation<ThemeProps<DefaultTheme>> | string;
}>`
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
  ${({ css }) => css}
`;

function Container({
  contentStyle,
  stickyBreadcrumbs,
  withSidebar = true,
  children,
  contentTopBarElement,
}: IContainerProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { developer } = useContext(ViewerContext);
  const sideBarItems = useMemo(
    () =>
      getSidebarItemsList({ developerType: developer?.type }).map(
        ({ label, icon, path, disabled }) => ({
          label: t(`labels.${label}`),
          icon,
          isActive: location.pathname.includes(path),
          disabled,
          onClick: () => navigate(path),
        })
      ),
    [developer, location.pathname, navigate, t]
  );

  return (
    <ContainerWrapper flexDirection="row" w="inherit">
      {/* Sidebar */}
      {renderBranch(withSidebar, <SideBar items={sideBarItems} />)}

      {/* Container */}
      <ContainerStyle flex={1} p={11} flexDirection="column" w="100%">
        <BreadcrumbWrapper
          alignItems="center"
          justifyContent="space-between"
          w="100%"
          pb={4}
          stickyBreadcrumbs={stickyBreadcrumbs}
        >
          <ContainerFlex>
            <BreadcrumbNavigation />
            {contentTopBarElement}
          </ContainerFlex>
        </BreadcrumbWrapper>

        {/* Content */}
        <Content css={contentStyle}>{children}</Content>
      </ContainerStyle>
    </ContainerWrapper>
  );
}

export default Container;
